<script setup>
// Services
import PublicHolidayService from "@/api/services/publicHoliday.service";
import HolidayService from "@/api/services/holiday.service";
import CommonService from "@/api/services/common.service";
import { getYear } from "@/views/production/mixin/common";
import moment from "moment";

// Components
import HeaderTitle from "@/components/layout/HeaderTitle";
import { computed, onMounted, ref } from "vue";
import { useAuthStore } from "@/app/stores/authStore";
import { useUiStore } from "@/app/stores/uiStore";

const authStore = useAuthStore();
const uiStore = useUiStore();

const disabledDates = ref([
  {
    repeat: {
      weekdays: [1, 7],
    },
  },
]);
const title = ref("Calendario");
const serverTimestamp = ref(null);
const currentServerYear = ref(null);
const dataLoaded = ref(false);
const minDate = ref(null);
const maxDate = ref(null);
const attributes = ref([]);
const publicHolidays = ref([]);
const employeeHolidays = ref([]);
const totalResults = ref(0);
const publicHolidayScope = ref("mine");
// const isMobile = ref(false);
const calendarRows = ref(3);
const calendarColumns = ref(6);

const publicHolidayScopeOptions = [
  { value: "mine", label: "Mis fiestas" },
  { value: "all", label: "Todas" },
];

const user = computed(() => authStore.getUserLogged);

const userWorkplaceId = computed(() => {
  return user.value?.workplace?.id || null;
});

onMounted(() => {
  resizeCalendar();
  window.addEventListener("resize", () => {
    resizeCalendar();
  });

  initializeCalendarData();
});

const resizeCalendar = () => {
  if (window.innerWidth <= 691) {
    calendarRows.value = 1;
    calendarColumns.value = 1;
  } else if (window.innerWidth <= 855) {
    calendarRows.value = 1;
    calendarColumns.value = 2;
  } else if (window.innerWidth <= 992) {
    calendarRows.value = 2;
    calendarColumns.value = 3;
  } else if (window.innerWidth <= 1300) {
    calendarRows.value = 4;
    calendarColumns.value = 3;
  } else {
    calendarRows.value = 3;
    calendarColumns.value = 4;
  }
};

/* Inicializa la vista */
const initializeCalendarData = async () => {
  await uiStore.startLoading();
  try {
    const resTimestamp = await CommonService.getServerTimestamp();
    serverTimestamp.value = resTimestamp.data.serverTimestamp;
    currentServerYear.value = getYear(serverTimestamp.value);
    title.value = "Calendario " + currentServerYear.value;

    // Configuramos la fecha mínima y máxima a mostrar en el calendario
    minDate.value = new Date(parseInt(currentServerYear.value), 0, 1);
    maxDate.value = new Date(parseInt(currentServerYear.value), 11, 31);

    await ensureUserWorkplace();

    dataLoaded.value = true;

    await uiStore.stopLoading();
    // Obtenemos los días festivos y las vacaciones aprobadas y los procesamos.
    await Promise.all([getPublicHolidays(), getEmployeeHolidays()]);
    buildCalendarAttributes();
  } catch {
    //  Notification.addNotification('Error al obtener el timestamp del servidor', 'error', 6000);
    uiStore.stopLoading();
  }
};



const ensureUserWorkplace = async () => {
  if (!user.value?.id || user.value.workplace) {
    return;
  }

  await authStore.setUserLogged(user.value);
};

/* Obtiene los dias festivos */
const getPublicHolidays = async () => {
  await uiStore.startLoading();
  try {
    const responses = await Promise.all(getPublicHolidayRequests());
    const holidaysByDateAndText = new Map();

    responses.forEach((res) => {
      (res?.data?.["hydra:member"] ?? []).forEach((publicHoliday) => {
        holidaysByDateAndText.set(getPublicHolidayKey(publicHoliday), publicHoliday);
      });
    });

    publicHolidays.value = Array.from(holidaysByDateAndText.values());
    totalResults.value = publicHolidays.value.length;
  } catch {
    publicHolidays.value = [];
    totalResults.value = 0;
  } finally {
    uiStore.stopLoading();
  }
};

const getPublicHolidayRequests = () => {
  const baseParams = {
    itemsPerPage: 99999,
    "date[before]": moment(maxDate.value).format("YYYY-MM-DD"),
    "date[after]": moment(minDate.value).format("YYYY-MM-DD"),
    "order[date]": "ASC",
  };

  if (publicHolidayScope.value === "all") {
    return [
      PublicHolidayService.getPublicHolidays(baseParams),
    ];
  }

  if (userWorkplaceId.value) {
    return [
      PublicHolidayService.getPublicHolidays({
        ...baseParams,
        "workplace.id": userWorkplaceId.value,
      }),
    ];
  }

  const nationalHolidaysRequest = PublicHolidayService.getPublicHolidays({
    ...baseParams,
    type: "nacional",
  });

  return [nationalHolidaysRequest];
};

/* Obtiene las vacaciones aprobadas del empleado logueado */
const getEmployeeHolidays = async () => {
  if (!user.value?.id) {
    employeeHolidays.value = [];
    return;
  }

  const holidayParams = {
    page: 1,
    itemsPerPage: 99999,
    "order[fromDate]": "desc",
    status: "approved",
    "fromDate[before]": moment(maxDate.value).format("YYYY-MM-DD"),
    "toDate[after]": moment(minDate.value).format("YYYY-MM-DD"),
  };

  uiStore.startLoading();
  try {
    const res = await HolidayService.getEmployeeHolidays(user.value.id, holidayParams);
    const annualHolidays = res?.data?.["hydra:member"] ?? [];
    employeeHolidays.value = annualHolidays.filter((holiday) => holiday?.status === "approved");

  } catch {
    employeeHolidays.value = [];
  } finally {
    uiStore.stopLoading();
  }
};

const getPublicHolidayLabel = (publicHoliday) => {
  if (publicHoliday.type === "nacional") {
    return publicHoliday.description;
  }

  const workplaceName = publicHoliday.workplace?.name || publicHoliday.workplaceName;
  return workplaceName
    ? `${workplaceName}: ${publicHoliday.description}`
    : publicHoliday.description;
};

const getPublicHolidayKey = (publicHoliday) => {
  const date = moment(publicHoliday.date).format("YYYY-MM-DD");
  const type = publicHoliday.type || "";
  const label = String(getPublicHolidayLabel(publicHoliday) || "").trim();

  return `${date}-${type}-${label}`;
};

const getEmployeeHolidayComment = (holiday) => {
  return holiday.observations || "Vacaciones";
};

const getCalendarDay = (calendarDays, date) => {
  const dateKey = moment(date).format("YYYY-MM-DD");

  if (!calendarDays[dateKey]) {
    calendarDays[dateKey] = [];
  }

  return calendarDays[dateKey];
};

const addLabelByDate = (calendarDays, date, label) => {
  const labels = getCalendarDay(calendarDays, date);
  const labelExists = labels.some((item) => item.text === label.text);

  if (!labelExists) {
    labels.push(label);
  }
};

const addHolidayRangeLabels = (calendarDays, holiday) => {
  const start = moment(holiday.fromDate);
  const end = moment(holiday.toDate);
  const comment = getEmployeeHolidayComment(holiday);
  const isSingleDayHoliday = start.isSame(end, "day");
  let current = start.clone();
  let holidayDay = 1;

  while (current.isSameOrBefore(end, "day")) {
    addLabelByDate(calendarDays, current, {
      text: isSingleDayHoliday ? comment : `${comment}, día ${holidayDay}`,
      type: "employee-holiday",
    });
    current = current.add(1, "day");
    holidayDay += 1;
  }
};

const getCalendarDays = () => {
  const calendarDays = {};

  addLabelByDate(calendarDays, new Date(), {
    text: "Hoy",
    type: "today",
  });

  publicHolidays.value.forEach((publicHoliday) => {
    addLabelByDate(calendarDays, publicHoliday.date, {
      text: getPublicHolidayLabel(publicHoliday),
      type: publicHoliday.type === "nacional" ? "national-holiday" : "local-holiday",
    });
  });

  employeeHolidays.value.forEach((holiday) => {
    addHolidayRangeLabels(calendarDays, holiday);
  });

  return calendarDays;
};

const getTodayAttribute = () => ({
  highlight: "green",
  dates: new Date(),
  rawDate: moment().format("YYYY-MM-DD"),
  order: 4,
});

const getPublicHolidayAttributes = () => publicHolidays.value.map((publicHoliday) => ({
  highlight: publicHoliday.type === "nacional" ? "red" : "blue",
  dates: new Date(publicHoliday.date),
  rawDate: publicHoliday.date,
  order: 3,
}));

const getEmployeeHolidayAttributes = () => employeeHolidays.value.map((holiday) => ({
  highlight: {
    color: "yellow",
    fillMode: "light",
  },
  dates: {
    start: moment(holiday.fromDate).toDate(),
    end: moment(holiday.toDate).toDate(),
  },
  order: 0,
}));

const getPopoverAttributes = () => {
  const calendarDays = getCalendarDays();

  return Object.entries(calendarDays).map(([dateKey, labels]) => ({
    customData: {
      labels,
    },
    popover: {
      label: labels.map((label) => label.text).join(" · "),
    },
    dates: moment(dateKey, "YYYY-MM-DD").toDate(),
    rawDate: dateKey,
    order: 5,
  }));
};

const buildCalendarAttributes = () => {
  attributes.value = [
    ...getEmployeeHolidayAttributes(),
    ...getPublicHolidayAttributes(),
    getTodayAttribute(),
    ...getPopoverAttributes(),
  ];
};

const handlePublicHolidayScopeChange = async () => {
  await getPublicHolidays();
  buildCalendarAttributes();
};
</script>

<template>
  <div class="employee-calendar">
    <!--        <h1>{{title}}</h1>-->
    <HeaderTitle :title="title" />
    <div class="employee-calendar-container">
      <!-- Leyenda -->
      <div class="row leyend-container d-flex align-items-center justify-content-between flex-wrap">
        <div class="calendar-legend d-flex align-items-center justify-content-start flex-nowrap">
          <div class="px-3">
            <label class="leyend-title">Leyenda:</label>
          </div>
          <div class="calendar-legend-items px-3 d-flex align-items-center justify-content-start">
            <div class="leyend-block d-flex align-items-center">
              <span class="dot green-dot" />
              <label class="leyend-label">Día actual</label>
            </div>
            <div class="leyend-block d-flex align-items-center">
              <span class="dot red-dot" />
              <label class="leyend-label">Festivo nacional</label>
            </div>
            <div class="leyend-block d-flex align-items-center">
              <span class="dot blue-dot" />
              <label class="leyend-label">Festivo local</label>
            </div>
            <div class="leyend-block d-flex align-items-center">
              <span class="dot yellow-dot" />
              <label class="leyend-label">Vacaciones</label>
            </div>
          </div>
        </div>
        <div v-if="userWorkplaceId" class="calendar-filter-wrapper px-3">
          <div class="calendar-filter form-item label-common">
            <label class="w-100">Festivos</label>
            <el-select v-model="publicHolidayScope" class="w-100" @change="handlePublicHolidayScopeChange">
              <el-option v-for="option in publicHolidayScopeOptions" :key="option.value" :label="option.label"
                :value="option.value" />
            </el-select>
          </div>
        </div>
      </div>

      <!-- Calendario -->
      <div v-if="dataLoaded" class="row calendar-row">
        <div class="col-12">
          <VCalendar id="holidays-calendar" is-expanded :rows="calendarRows" :columns="calendarColumns"
            :min-date="minDate" :max-date="maxDate" :attributes="attributes" :first-day-of-week="2"
            :disabled-dates="disabledDates" nav-visibility="hidden">
            <template #day-popover="{ dayTitle, attributes: popoverAttributes }">
              <div class="employee-calendar-popover">
                <div class="employee-calendar-popover-title">
                  {{ dayTitle }}
                </div>
                <template v-for="attribute in popoverAttributes" :key="attribute.key">
                  <div v-for="label in attribute.customData.labels"
                    :key="`${attribute.key}-${label.type}-${label.text}`" class="employee-calendar-popover-line">
                    <span :class="['employee-calendar-popover-dot', `employee-calendar-popover-dot--${label.type}`]" />
                    <span class="employee-calendar-popover-text">{{ label.text }}</span>
                  </div>
                </template>
              </div>
            </template>
          </VCalendar>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.employee-calendar {
  margin-bottom: 20px;

  .leyend-container {
    margin-top: 25px;
    margin-bottom: 25px;
  }

  // .leyend-label {
  //   vertical-align: bottom;
  // }

  .leyend-block {
    display: flex;
    align-items: center;
    margin-right: 20px;
  }

  .dot {
    height: 25px;
    width: 25px;
    min-width: 25px;
    min-height: 25px;
    border-radius: 50%;
    display: inline-block;
    margin-right: 5px;
  }

  .red-dot {
    background-color: #e53e3e;
  }

  .blue-dot {
    background-color: #3182ce;
  }

  .green-dot {
    background-color: #38a169;
  }

  .yellow-dot {
    background-color: #f7d20e;
  }

  .calendar-legend {
    flex: 1 1 auto;
    min-width: 0;
  }

  .calendar-legend-items {
    flex-wrap: nowrap;
    min-width: 0;
  }

  .calendar-filter-wrapper {
    flex: 0 0 auto;
    align-self: center;
  }

  .calendar-filter {
    width: 180px;
    margin-bottom: 0 !important;
  }

}

:global(.employee-calendar-popover) {
  width: max-content;
  max-width: 200px;
  box-sizing: border-box;
  padding: 2px 7px;
  margin: 0 auto;
}

:global(.vc-popover-content-wrapper),
:global(.vc-popover-content) {
  z-index: 1000 !important;
}

:global(.vc-popover-content .employee-calendar-popover-title) {
  margin-bottom: 4px;
  color: #7a8398;
  font-size: 12px !important;
  line-height: 1.15 !important;
  font-weight: 700;
  text-align: center;
  text-transform: capitalize;
}

:global(.employee-calendar-popover-line) {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 5px;
  margin-top: 2px;
  max-width: 164px;
  box-sizing: border-box;
  padding: 0;
}

:global(.vc-popover-content .employee-calendar-popover-text) {
  display: inline-block;
  min-width: 0;
  max-width: 150px;
  color: inherit;
  font-size: 12.5px !important;
  line-height: 1.18 !important;
  font-weight: inherit;
  text-align: left;
  white-space: normal;
  word-break: break-word;
}

:global(.employee-calendar-popover-dot) {
  width: 6px;
  height: 6px;
  min-width: 6px;
  border-radius: 50%;
  background-color: #7a8398;
}

:global(.employee-calendar-popover-dot--today) {
  background-color: #38a169;
}

:global(.employee-calendar-popover-dot--national-holiday) {
  background-color: #e53e3e;
}

:global(.employee-calendar-popover-dot--local-holiday) {
  background-color: #3182ce;
}

:global(.employee-calendar-popover-dot--employee-holiday) {
  background-color: #f7d20e;
}

.leyend-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

@media (max-width: 767px) {
  .leyend-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .calendar-legend {
    width: 100%;
    align-items: flex-start !important;
    flex-direction: column;
  }

  .calendar-legend-items {
    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 12px;
    width: 100%;
  }

  .leyend-block {
    margin-right: 0;
    width: 100%;
    display: flex;
    align-items: center;
  }

  .dot {
    height: 20px;
    width: 20px;
    min-width: 20px;
    min-height: 20px;
  }

  .calendar-filter-wrapper {
    width: 100%;
  }

  .calendar-filter {
    width: 100%;
  }
}

@media (min-width: 768px) {
  :global(#holidays-calendar .vc-header .vc-arrow) {
    display: none !important;
  }
}

@media (max-width: 767px) {
  :global(#holidays-calendar .vc-header .vc-arrow:disabled) {
    visibility: hidden;
  }
}

@media (max-width: 767px) {
  .leyend-title {
    font-size: 24px;
  }

  .leyend-label {
    font-size: 13px;
  }

  .leyend-block {
    margin-right: 0px !important;
  }

  .employee-calendar {
    #header-title {
      height: 80px !important;
    }
  }
}
</style>
